import { cn } from '@jeraisonne/ui';
import type { SchoolLevel } from '@jeraisonne/db';
import type { DecouverteProfile } from '@/features/decouverte/decouverte-profile';
import { DECOUVERTE_TRACKS, DECOUVERTE_TRACK_ORDER } from '@/features/decouverte/decouverte-slides';
import { DEMO_LEVELS, LEVEL_LABEL } from '../types';
// `jr-stagger` et le fondu d'un écran à l'autre
import './kit-stage.css';

/** La question posée selon le profil choisi à l'accueil (un parent choisit la classe de son enfant). PUR. */
export function levelQuestionFor(profile: DecouverteProfile | null): {
  title: string;
  text: string;
} {
  const end = 'guidé pas à pas sur la feuille de brouillon. Une minute, sans compte.';
  if (profile === 'parent')
    return {
      title: 'En quelle classe est ton enfant ?',
      text: `Un vrai exercice de sa classe, ${end}`,
    };
  if (profile === 'tutor' || profile === 'player')
    return {
      title: 'Quelle classe veux-tu essayer ?',
      text: `Un vrai exercice de cette classe, ${end}`,
    };
  return { title: 'En quelle classe es-tu ?', text: `Un vrai exercice de ta classe, ${end}` };
}

/**
 * Premier écran de la démo sans compte : d'abord QUI on est si l'accueil `/decouverte` ne l'a pas
 * déjà dit (retour de Philippe du 5/11/2026) — la question de la classe n'est pas la même pour un
 * élève, un parent ou un prof, et poser « En quelle classe es-tu ? » à un parent sonne faux. Les
 * quatre profils, leurs libellés et leurs sous-titres sont ceux de l'accueil : une seule source.
 */
export function ProfileSelectionScreen({
  onPick,
}: {
  onPick: (profile: DecouverteProfile) => void;
}) {
  return (
    <div className="jr-stagger mx-auto max-w-lg space-y-6" data-testid="demo-profile">
      <header className="space-y-2">
        <h1 className="font-display text-ink-900 text-3xl font-extrabold leading-tight">
          Tu es… ?
        </h1>
        <p className="text-ink-600 text-[15px]">
          Pour te montrer le bon exercice. Une minute, sans compte.
        </p>
      </header>
      <div role="group" aria-label="Profil" className="grid gap-2.5">
        {DECOUVERTE_TRACK_ORDER.map((p) => {
          const track = DECOUVERTE_TRACKS[p];
          return (
            <button
              key={p}
              type="button"
              onClick={() => onPick(p)}
              className="border-ink-200 hover:border-primary-300 hover:bg-primary-50/40 ocean:bg-white/5 flex min-h-[3.5rem] items-center gap-3 rounded-2xl border bg-white px-4 py-3 text-left transition-colors active:scale-[0.98]"
            >
              <span aria-hidden className="text-2xl">
                {track.emoji}
              </span>
              <span className="min-w-0">
                <span className="text-ink-900 block text-base font-bold">{track.label}</span>
                <span className="text-ink-500 block truncate text-[13px]">{track.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Puis la classe du visiteur, en un toucher — et la vraie question de démo de cette classe, guidée
 * pas à pas (`/demo`) : un 6e et un Terminale ne voient pas le même exercice.
 */
export function LevelSelectionScreen({
  onSelect,
  onSignup,
  onChangeProfile,
  profile = null,
}: {
  onSelect: (level: SchoolLevel) => void;
  onSignup: () => void;
  /** revenir à « Tu es… ? » (le profil a été choisi trop vite, ou on teste pour quelqu'un d'autre) */
  onChangeProfile?: () => void;
  /** profil choisi à l'accueil `/decouverte` ou juste avant */
  profile?: DecouverteProfile | null;
}) {
  const q = levelQuestionFor(profile);
  return (
    <div className="jr-stagger mx-auto max-w-lg space-y-6" data-testid="demo-level">
      <header className="space-y-2">
        <h1 className="font-display text-ink-900 text-3xl font-extrabold leading-tight">
          {q.title}
        </h1>
        <p className="text-ink-600 text-[15px]">{q.text}</p>
      </header>
      <div role="group" aria-label="Classe" className="grid grid-cols-3 gap-2.5">
        {DEMO_LEVELS.map((level) => (
          <button
            key={level}
            type="button"
            onClick={() => onSelect(level)}
            className={cn(
              'border-ink-200 hover:border-primary-300 hover:bg-primary-50/40 ocean:bg-white/5 flex min-h-[3.25rem] items-center justify-center rounded-2xl border bg-white px-3 py-3 text-base font-bold transition-colors active:scale-[0.97]',
              level === 'tle' && 'col-span-3 sm:col-span-1',
            )}
          >
            {LEVEL_LABEL[level]}
          </button>
        ))}
      </div>

      {profile && onChangeProfile && (
        <p className="text-ink-500 text-center text-sm">
          Profil : {DECOUVERTE_TRACKS[profile].label} ·{' '}
          <button
            type="button"
            onClick={onChangeProfile}
            className="text-primary-600 min-h-11 font-semibold underline-offset-4 hover:underline"
          >
            Changer de profil
          </button>
        </p>
      )}

      <p className="text-ink-500 text-center text-sm">
        Déjà convaincu ?{' '}
        <button
          type="button"
          onClick={onSignup}
          className="text-primary-600 min-h-11 font-semibold underline-offset-4 hover:underline"
        >
          Créer mon compte gratuit
        </button>
      </p>
    </div>
  );
}
